import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { t } from '@/i18n';
import { tourSteps } from './tourSteps';

const SEEN_KEY = 'omya_tour_seen';
const TourContext = createContext({ startTour: () => {} });
export const useTour = () => useContext(TourContext);

const isSeen = () => { try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return true; } };
const markSeen = () => { try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ } };

// Resolves once the selector is in the DOM (pages render after navigation).
function waitFor(selector, timeout = 4000) {
  return new Promise(resolve => {
    const started = Date.now();
    const poll = () => {
      const el = document.querySelector(selector);
      if (el || Date.now() - started > timeout) resolve(el);
      else setTimeout(poll, 100);
    };
    poll();
  });
}

const actions = {
  async runUrgency() {
    document.querySelector('[data-tour="urgency-run"]')?.click();
    const approve = await waitFor('[data-tour="urgency-approve"]', 1500);
    approve?.click();
  },
};

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef(null);
  const busy = useRef(false);

  const goTo = useCallback(async (index) => {
    const d = driverRef.current;
    const step = tourSteps[index];
    if (!d || !step) { d?.destroy(); return; }
    // Ignore extra clicks while the next page or element is still loading.
    if (busy.current) return;
    busy.current = true;
    try {
      if (window.location.pathname !== step.route) navigate(step.route);
      if (step.action) await actions[step.action]();
      if (step.element) await waitFor(step.element);
      // drive() initialises driver.js (overlay, keyboard, close); later steps just move.
      if (d.isActive()) d.moveTo(index); else d.drive(index);
    } finally {
      busy.current = false;
    }
  }, [navigate]);

  const startTour = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = driver({
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      nextBtnText: t('tour.next'),
      prevBtnText: t('tour.prev'),
      doneBtnText: t('tour.done'),
      popoverClass: 'omya-tour',
      steps: tourSteps.map(s => ({
        element: s.element,
        popover: { title: t(`tour.${s.id}.title`), description: t(`tour.${s.id}.text`), side: s.side },
      })),
      onNextClick: (_el, _step, { state }) => goTo(state.activeIndex + 1),
      onPrevClick: (_el, _step, { state }) => goTo(state.activeIndex - 1),
    });
    goTo(0);
  }, [goTo]);

  // First visit after login: start on its own once the arrivals page is up.
  useEffect(() => {
    if (location.pathname !== '/kamiony' || isSeen() || driverRef.current) return undefined;
    // Marked as seen right away: closing the tab midway must not restart it on every visit.
    const timer = setTimeout(() => { markSeen(); startTour(); }, 800);
    return () => clearTimeout(timer);
  }, [location.pathname, startTour]);

  return <TourContext.Provider value={{ startTour }}>{children}</TourContext.Provider>;
}
